import { api } from './client'
import type { ApiResponse } from './client'
import { API_BASE_URL } from '@/lib/config'

export interface Product {
    id: string
    sku: string
    barcode?: string
    /** Mã hàng KiotViet đã map với SP này (màn mapping). Trống = chưa kết nối. */
    kiotviet_sku?: string
    /** Tên SP KiotViet của mã đang nối — chỉ API gợi ý nối SP trả (M-145). */
    kiotviet_name?: string
    /** Mã đang nối không còn trên KiotViet — chỉ API gợi ý nối SP trả. */
    kiotviet_missing?: boolean
    name: string
    description?: string
    unit: string
    cost_price: number
    unit_price: number
    weight_value?: number
    weight_unit?: string
    /** Danh mục đóng gói (#M-16). Rỗng = chưa xếp danh mục. */
    category_id?: string
    /** Tên/mã danh mục do server trả kèm, khỏi phải gọi thêm API. */
    category_name?: string
    category_code?: string
    product_type?: 'unit' | 'combo'
    is_active: boolean
    is_sellable: boolean
    /** Dùng làm nguồn để đóng gói thành sản phẩm khác (vd túi lẻ). */
    is_packaging_source?: boolean
    /** Mức tồn tối thiểu để cảnh báo loa máy trạm. 0 = tắt cảnh báo. */
    min_stock_level?: number
    // price_includes_material: giá bán đã gồm giá vật tư → bật nút tách/gộp khi lên đơn.
    price_includes_material?: boolean
    // material_price_per_unit: BE tính Σ(sale_price × BOM.quantity)/1 SP. Dùng cho
    // tách/gộp: giá_tách = unit_price − material_price_per_unit. 0 nếu không có vật tư giá.
    material_price_per_unit?: number
    created_at: string
    updated_at: string
    images?: ProductImage[]
    /** Tổng tồn kho (SUM quantity_available mọi lô). Chỉ có ở list `/products`;
     *  các endpoint khác (getById, barcode) trả 0. */
    stock_available?: number
    /** Phần tồn đang nằm trên kệ bán POS (vị trí gắn `is_pos_shelf`). Cùng
     *  nguồn với `stock_available`; 0 khi chưa gắn kệ POS nào. */
    pos_stock_available?: number
}

export interface ProductImage {
    id: string
    product_id: string
    image_url: string
    sort_order: number
    is_primary: boolean
    created_at: string
}

export interface CreateProductPayload {
    sku: string
    name: string
    barcode?: string
    description?: string
    unit: string
    cost_price: number
    unit_price: number
    weight_value?: number
    weight_unit?: string
    /** Danh mục đóng gói; chuỗi rỗng = chưa xếp danh mục. */
    category_id?: string
    is_active?: boolean
    is_sellable?: boolean
    is_packaging_source?: boolean
    /** Mức tồn tối thiểu để cảnh báo loa máy trạm. 0 = tắt cảnh báo. */
    min_stock_level?: number
    price_includes_material?: boolean
    /** Người dùng đã đồng ý ngừng hoạt động SP đang giữ mã vạch (popup trùng
     *  mã, M-156); id lấy từ params.id của lỗi BARCODE_EXISTS. */
    deactivate_barcode_holder_id?: string
}

export interface UpdateProductPayload {
    sku?: string
    name?: string
    barcode?: string
    description?: string
    unit?: string
    cost_price?: number
    unit_price?: number
    weight_value?: number
    weight_unit?: string
    /** Danh mục đóng gói; gửi chuỗi rỗng để GỠ danh mục khỏi sản phẩm. */
    category_id?: string
    is_active?: boolean
    is_sellable?: boolean
    is_packaging_source?: boolean
    /** Mức tồn tối thiểu để cảnh báo loa máy trạm. 0 = tắt cảnh báo. */
    min_stock_level?: number
    price_includes_material?: boolean
    /** Người dùng đã đồng ý ngừng hoạt động SP đang giữ mã vạch (popup trùng
     *  mã, M-156); id lấy từ params.id của lỗi BARCODE_EXISTS. */
    deactivate_barcode_holder_id?: string
}

/** Một ô tồn đã gộp theo (kho, vị trí, mức CK đánh dấu). Ô phẳng — FE tự xoay
 *  thành ma trận vị trí × chiết khấu. */
export interface ProductStockCell {
    warehouse_id: string
    warehouse_name: string
    /** null khi lô chưa được gán vị trí; tồn của nó vẫn tính vào total_available. */
    position_id: string | null
    position_code: string | null
    position_name: string | null
    /** Mức CK đánh dấu của các lô trong ô (0 = không đánh dấu). */
    discount_percent: number
    quantity: number
    lot_count: number
}

export interface ProductStockBreakdown {
    product_id: string
    unit: string
    total_available: number
    cells: ProductStockCell[]
}

export interface ProductImportResult {
    imported: number
    skipped: number
    errors: { row: number; sku: string; error: string }[]
}

export const productApi = {
    list: (params?: Record<string, string | number | boolean | undefined>) =>
        api.get<Product[]>('/products', { params }),
    getById: (id: string) => api.get<Product>(`/products/${id}`),
    getStockBreakdown: (id: string) =>
        api.get<ProductStockBreakdown>(`/products/${id}/stock-breakdown`),
    getByBarcode: (barcode: string) =>
        api.get<Product>(`/products/barcode/${encodeURIComponent(barcode)}`),
    create: (data: CreateProductPayload) =>
        api.post<Product>('/products', data),
    update: (id: string, data: UpdateProductPayload) =>
        api.put<Product>(`/products/${id}`, data),
    delete: (id: string) => api.delete(`/products/${id}`),
    uploadImage: async (
        productId: string,
        file: File,
    ): Promise<ApiResponse<ProductImage>> => {
        const formData = new FormData()
        formData.append('image', file)
        const res = await fetch(
            `${API_BASE_URL}/products/${productId}/images`,
            {
                method: 'POST',
                body: formData,
                credentials: 'include',
            },
        )
        const data = (await res.json()) as ApiResponse<ProductImage>
        if (!res.ok) {
            throw new Error(data.error?.message || 'Upload failed')
        }
        return data
    },
    deleteImage: (productId: string, imageId: string) =>
        api.delete(`/products/${productId}/images/${imageId}`),
    setPrimaryImage: (productId: string, imageId: string) =>
        api.put(`/products/${productId}/images/${imageId}/primary`),
    importExcel: (file: File) => {
        const formData = new FormData()
        formData.append('file', file)
        return api.post<ProductImportResult>('/products/import', formData)
    },
    /** Gán/gỡ mã KiotViet cho SP (null = gỡ). Màn mapping KiotViet. */
    setKiotVietSku: (id: string, kiotviet_sku: string | null) =>
        api.put(`/products/${id}/kiotviet-sku`, { kiotviet_sku }),
    /** Tìm sản phẩm bên KiotViet để nối (picker). */
    searchKiotViet: (name: string) =>
        api.get<KiotVietProduct[]>('/kiotviet-products', {
            params: { name: name || undefined },
        }),
    /** Dữ liệu màn nối SP (bảng): SP ERP chưa map + SP KiotViet chưa map + gợi ý. */
    getKiotVietMapping: () =>
        api.get<KiotVietMappingData>('/kiotviet-products/mapping'),
    /** Gợi ý SP ERP tương đồng cho một dòng KiotViet chưa nối (M-145): cả SP
     *  đã nối lẫn chưa nối, xếp theo điểm khớp, tối đa ~10. */
    getKiotVietCandidates: (code: string, name: string) =>
        api.get<Product[]>('/kiotviet-products/candidates', {
            params: { code, name },
        }),
    /** Tên SP KiotViet theo mã (nhãn "Đang nối"). `missing` = mã không còn
     *  trên KiotViet. */
    getKiotVietNames: (codes: string[]) =>
        api.get<KiotVietCodeName[]>('/kiotviet-products/names', {
            params: { codes: codes.join(',') },
        }),
    /** Lưu nhiều mapping một lượt. Trả về số đã lưu + SP không lưu được. */
    saveKiotVietMappings: (mappings: KiotVietMappingInput[]) =>
        api.put<KiotVietBulkResult>('/products/kiotviet-sku/bulk', {
            mappings,
        }),
}

/** Tên SP KiotViet của một mã (M-145). */
export interface KiotVietCodeName {
    code: string
    name?: string
    missing: boolean
}

/** SP ERP ở cột trái. `kiotviet_sku` = mã đang nối ('' = chưa nối). */
export interface ErpMappingProduct {
    id: string
    name: string
    sku: string
    kiotviet_sku: string
}

/** Một cặp (SP ERP, mã KiotViet) gửi lên để lưu. `kiotviet_sku` rỗng = gỡ nối. */
export interface KiotVietMappingInput {
    product_id: string
    kiotviet_sku: string
}

/** Toàn bộ dữ liệu màn nối sản phẩm. */
export interface KiotVietMappingData {
    /** TẤT CẢ SP ERP (đã nối lẫn chưa nối) — FE lọc theo tab. */
    erp_products: ErpMappingProduct[]
    /** TOÀN BỘ catalog KiotViet (kể cả mã đã nối, cho phép đổi/cướp mapping). */
    kiotviet_products: KiotVietProduct[]
    /** Gợi ý khớp cao cho SP chưa nối. */
    suggestions: KiotVietMappingInput[]
}

/** Kết quả lưu hàng loạt. `failed` = id SP không lưu được (mã đã bị chiếm…). */
export interface KiotVietBulkResult {
    saved: number
    failed: string[]
}

/** Sản phẩm bên KiotViet (subset cho picker mapping). `code` = mã lưu vào
 *  Product.kiotviet_sku. */
export interface KiotVietProduct {
    id: number
    code: string
    name: string
    fullName: string
    categoryName: string
    basePrice: number
    inventories?: { branchName: string; onHand: number }[]
}
